import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import type { CallInvite, LiveCallBooking } from '../types/liveCall';

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateToken(): string {
  // 32-char hex token — not cryptographically hardened on the client, but fine
  // for a join link backed by Firestore security rules that check the token field.
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function normalize(data: Record<string, unknown>, id: string): CallInvite {
  const toISO = (v: unknown): string | undefined => {
    if (v instanceof Timestamp) return v.toDate().toISOString();
    if (typeof v === 'string') return v;
    return undefined;
  };
  return {
    ...(data as unknown as CallInvite),
    id,
    createdAt: toISO(data.createdAt) ?? new Date().toISOString(),
    sentAt: toISO(data.sentAt),
    joinedAt: toISO(data.joinedAt),
  };
}

// ── Create ────────────────────────────────────────────────────────────────────

/**
 * Create a call invite and write it in two places:
 *   1. `callInvites/{inviteId}`  — top-level; Cloud Function picks this up to send the email.
 *   2. `users/{playerId}/callInvites/{inviteId}` — denormalized so the player's account shows
 *      all stakeholders they've ever invited to calls.
 *
 * Returns the new invite (including the join token for building the link).
 */
export async function createCallInvite(
  bookingId: string,
  coachId: string,
  playerId: string,
  invitedEmail: string,
  invitedName?: string,
): Promise<CallInvite> {
  const inviteRef = doc(collection(db, 'callInvites'));
  const id = inviteRef.id;
  const token = generateToken();

  const invite: Omit<CallInvite, 'id'> = {
    bookingId,
    coachId,
    playerId,
    invitedEmail: invitedEmail.trim().toLowerCase(),
    invitedName,
    token,
    status: 'pending',
    createdAt: new Date().toISOString(),
  };

  const cleaned = Object.fromEntries(Object.entries(invite).filter(([, v]) => v !== undefined));

  // Write to both paths concurrently
  const userInviteRef = doc(db, 'users', playerId, 'callInvites', id);
  await Promise.all([
    setDoc(inviteRef, { ...cleaned, createdAt: serverTimestamp() }),
    setDoc(userInviteRef, { ...cleaned, createdAt: serverTimestamp() }),
  ]);

  return { ...invite, id };
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Invites for a specific booking (shown in the in-call invite panel). The
 * rules let only the lesson's coach and player read them, so the query asks
 * for the caller's own side.
 */
export async function getInvitesForBooking(
  bookingId: string, coachId: string, playerId: string,
): Promise<CallInvite[]> {
  const uid = auth.currentUser?.uid;
  if (!uid || (uid !== coachId && uid !== playerId)) return [];
  const q = query(
    collection(db, 'callInvites'),
    where('bookingId', '==', bookingId),
    where(uid === coachId ? 'coachId' : 'playerId', '==', uid),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalize(d.data(), d.id));
}

/** All invite history for a player (used to prefill the email field next time). */
export async function getPlayerInviteHistory(playerId: string): Promise<CallInvite[]> {
  const q = query(collection(db, 'users', playerId, 'callInvites'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalize(d.data(), d.id));
}

/**
 * Validate a join token for an invited guest.
 * Returns the invite if the token is valid, null otherwise.
 */
export async function validateInviteToken(
  bookingId: string,
  token: string,
): Promise<CallInvite | null> {
  // Signed in: the rules let a signed-in user read an invite by its id. A
  // signed-out guest's link goes through getGuestInvite instead.
  const snap = await getDoc(doc(db, 'callInvites', token));
  if (!snap.exists()) return null;
  const invite = normalize(snap.data(), snap.id);
  if (invite.bookingId !== bookingId) return null;
  return invite;
}

// ── Update ────────────────────────────────────────────────────────────────────

/** Mark an invite as joined when the guest opens the call room with their token. */
export async function markInviteJoined(inviteId: string): Promise<void> {
  await updateDoc(doc(db, 'callInvites', inviteId), {
    status: 'joined',
    joinedAt: serverTimestamp(),
  });
}

/**
 * The booking a guest's invite link opens, signed out (the rules let only a
 * signed-in user read callInvites). The server checks the token and returns
 * the booking id and nothing else; an unknown or dead link is not-found.
 */
export async function getGuestInvite(token: string): Promise<{ bookingId: string }> {
  const res = await httpsCallable<{ token: string }, { bookingId: string }>(
    functions, 'getLiveCallInvite',
  )({ token });
  return res.data;
}

/**
 * A signed-in guest's room: the lesson its invite is for (the rules let
 * only the pair read a booking). The times and names the room shows.
 */
export async function getGuestLesson(bookingId: string, token: string): Promise<LiveCallBooking> {
  const res = await httpsCallable<{ bookingId: string; token: string }, { booking: LiveCallBooking }>(
    functions, 'getLiveGuestLesson',
  )({ bookingId, token });
  return res.data.booking;
}
